import * as Device from 'expo-device'
import * as Linking from 'expo-linking'
import * as Notifications from 'expo-notifications'
import { useCallback, useEffect, useState, type FC, type ReactNode } from 'react'

import { Dialog } from '@/components'
import { NotificationsContext } from '@/contexts'
import {
  useAddUserPushTokenMutation,
  useAppState,
  useClearUserPushTokensMutation,
  useDisclosure,
  usePreloadableRouter
} from '@/hooks'
import { IS_ANDROID, IS_WEB, notificationsEventEmitter } from '@/lib'

if (!IS_WEB) {
  // noinspection JSUnusedGlobalSymbols
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldPlaySound: true,
      shouldSetBadge: true,
      shouldShowBanner: true,
      shouldShowList: true
    })
  })
}

export interface NotificationsProviderProps {
  children: ReactNode
}

export const NotificationsProvider: FC<NotificationsProviderProps> = ({ children }) => {
  const { expoPushToken, setExpoPushToken } = useAppState()
  const router = usePreloadableRouter()
  const [showPermissionsDialog, permissionsDialog] = useDisclosure(false)
  const [isEnablingNotifications, setIsEnablingNotifications] = useState(false)
  const [addUserPushToken] = useAddUserPushTokenMutation()
  const [clearUserPushTokens, isDisablingNotifications] = useClearUserPushTokensMutation()
  const setBadgeCountAsync = useCallback(async (count: number) => {
    if (IS_WEB) {
      return
    }

    await Notifications.setBadgeCountAsync(count)
  }, [])
  const disableNotificationsAsync = useCallback(async () => {
    // first, get rid of any tokens we have saved in the db
    await clearUserPushTokens()
    // next, unset our local token stored in state, which will trigger the useEffect below
    await setExpoPushToken(null)
    // last, clear any badge counts
    await setBadgeCountAsync(0)
  }, [clearUserPushTokens, setExpoPushToken, setBadgeCountAsync])
  const enableNotificationsAsync = useCallback(async () => {
    // keep track of whether we are push enabled or not
    let enabled = false

    // push notifications don't work on simulators, so don't bother if we are not on device
    if (Device.isDevice) {
      setIsEnablingNotifications(true)

      // you have to have at least one "channel" for android, so configure that now
      if (IS_ANDROID) {
        await Notifications.setNotificationChannelAsync('default', {
          name: 'default',
          importance: Notifications.AndroidImportance.MAX
        })
      }

      // now, check to see if we already have permission. this doesn't trigger the request dialog, it only checks for
      // existing permissions
      const { status: existingStatus } = await Notifications.getPermissionsAsync()

      let finalStatus = existingStatus

      // if they have previously denied access in the past, we can only prompt the user to re-enable permissions in the
      // OS settings, we can't do anything else until they do
      if (existingStatus === 'denied') {
        permissionsDialog.open()
      } else if (existingStatus === 'undetermined') {
        // if we've never asked before, let's do so now!
        const { status } = await Notifications.requestPermissionsAsync()

        finalStatus = status
      }

      if (__DEV__) {
        console.debug('[expo push] permissions', finalStatus)
      }

      // either we already had it, or they just allowed it, but we have permissions now
      if (finalStatus === 'granted') {
        // grab the expo push token, which is not the same thing as a "device token" and only works with expo's push
        // services
        const { data: nextExpoPushToken } = await Notifications.getExpoPushTokenAsync()

        if (nextExpoPushToken) {
          if (__DEV__) {
            console.debug('[expo push] got token', nextExpoPushToken)
          }

          // save it in our local state
          await setExpoPushToken(nextExpoPushToken)
          // upsert it into the list of tokens we are storing in the db for this user
          const response = await addUserPushToken(nextExpoPushToken)

          await setBadgeCountAsync(response?.addUserPushToken?.query?.currentUserUnreadCount ?? 0)

          // we are enabled for push notifications!
          enabled = true
        }
      }

      setIsEnablingNotifications(false)
    } else if (__DEV__) {
      // remind any dev watching the console that we can't do push on simulators!
      console.warn('[expo push] cannot enable on simulator, skipping')
    }

    // let our caller know whether we are enabled for push notifications or not
    return enabled
  }, [addUserPushToken, permissionsDialog, setBadgeCountAsync, setExpoPushToken])

  useEffect(() => {
    if (!IS_WEB && expoPushToken) {
      const handleNotificationRequest = async (notification: Notifications.Notification) => {
        if (__DEV__) {
          console.debug('[expo push] notification received', notification)
        }

        switch (notification.request.content.data?.type) {
          case 'LIKE':
            return notificationsEventEmitter.emit('LIKE')
          case 'MATCH':
            return notificationsEventEmitter.emit('MATCH')
          case 'MESSAGE':
            return notificationsEventEmitter.emit('MESSAGE')
          case 'SPARK':
            return notificationsEventEmitter.emit('SPARK')
        }
      }
      const handleNotificationResponse = async (response: Notifications.NotificationResponse | null) => {
        if (__DEV__) {
          console.debug('[expo push] notification response', response)
        }

        if (response) {
          switch (response.notification.request.content.data?.type) {
            case 'LIKE':
              if (response.notification.request.content.data.likeId) {
                router.push(`/likes/${response.notification.request.content.data.likeId}`, {
                  queryName: 'LikeProfileScreenQuery',
                  queryVariables: {
                    likeId: response.notification.request.content.data.likeId
                  }
                })
              }

              break
            case 'MATCH':
              router.push('/messages', {
                queryName: 'MessagesScreenQuery'
              })
              break

            case 'MESSAGE':
              if (response.notification.request.content.data.matchId) {
                router.push(`/messages/${response.notification.request.content.data.matchId}`, {
                  queryName: 'MatchMessagesScreenQuery',
                  queryVariables: {
                    matchId: response.notification.request.content.data.matchId
                  }
                })
              }

              break

            case 'SPARK':
              if (response.notification.request.content.data.likeId) {
                router.push(`/likes/${response.notification.request.content.data.likeId}`, {
                  queryName: 'LikeProfileScreenQuery',
                  queryVariables: {
                    likeId: response.notification.request.content.data.likeId
                  }
                })
              }

              break

            case 'VERIFICATION_REQUEST':
              router.push('/profile/verify', {
                fetchPolicy: 'store-and-network',
                queryName: 'VerificationPhotosScreenQuery'
              })

              break
          }

          // after handling a notification response, we need to clear it, otherwise it will keep showing up if the user
          // suspends or terminates the app
          await Notifications.clearLastNotificationResponseAsync()
          // since the user responded to this notification, we can dismiss it at the OS level, so it doesn't show up in
          // the lockscreen or notifications area/tray
          await Notifications.dismissNotificationAsync(response.notification.request.identifier)
        }
      }
      // first, set up a listener to handle when a notification is received, so we can let emit the appropriate events
      const notificationReceivedListener = Notifications.addNotificationReceivedListener(handleNotificationRequest)
      // next, set up a listener to handle when a user "responds" to a notification while it is active, e.g. by tapping
      // on the notification overlay or in the notifications tray
      const notificationResponseReceivedListener =
        Notifications.addNotificationResponseReceivedListener(handleNotificationResponse)

      // next, check to see if there is a "pending" response, which happens if the app is terminated or suspended when
      // the user tapped on the notification and the app had to be started or resumed
      Notifications.getLastNotificationResponseAsync().then(handleNotificationResponse)

      return () => {
        // always kill the subscriptions if we either unmount or if the current token was unset, otherwise memory leaks
        notificationReceivedListener.remove()
        notificationResponseReceivedListener.remove()
      }
    }
  }, [expoPushToken, router])

  return (
    <NotificationsContext.Provider
      value={{
        disableNotificationsAsync,
        enableNotificationsAsync,
        isDisablingNotifications,
        isEnablingNotifications,
        setBadgeCountAsync
      }}
    >
      {children}
      {!IS_WEB ? (
        <Dialog
          confirmLabel='Open Settings'
          isOpen={showPermissionsDialog}
          onClose={permissionsDialog.close}
          onConfirm={async () => {
            permissionsDialog.close()
            await Linking.openSettings()
          }}
          title='Notifications Disabled'
        >
          Notifications appear to be disabled on your device for Dateability! You must enable them in your phone's
          settings and then try again.
        </Dialog>
      ) : null}
    </NotificationsContext.Provider>
  )
}
